import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { MaterialVersion, MaterialVersionForm, MaterialVersionSearchForm } from 'entities/material_version'

type MaterialVersionsResponse = PagingResponse & {
    results: MaterialVersion[]
}

export function useFetchMaterialVersionsApi(searchForm?: Form<MaterialVersionSearchForm>): IndexApiSet<MaterialVersionsResponse> & { execute: () => void } {
    const apiPath = 'material_versions/'
    const api = useIndexApi<MaterialVersionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostMaterialVersionApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialVersionForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MaterialVersionForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaterialVersionForm>) => {
        const apiPath = `material_versions/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMaterialVersionApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialVersionForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MaterialVersionForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaterialVersionForm>) => {
        const apiPath = `material_versions/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutMaterialVersionsApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialVersionForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, MaterialVersionForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MaterialVersionForm[]>) => {
        const apiPath = `material_versions/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMaterialVersionApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `material_versions/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
